import { EMPTY_GROUP_SORT_ORDER_SENTINEL } from "@common/constants/domains/accounting/accountGroups";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { getGroupDisplayKey } from "./chartRowDisplayKeys";

export interface MergedGroup {
    group: AccountGroup;
    groups: AccountGroup[];
    mergedSortOrder: number;
    key: string;
    parentKey: string | null;
}

// First-pass accumulator: constituents are collected here before their shared
// `mergedSortOrder` and representative `group` are computed, so those fields are
// assigned exactly once (see the reduction below) rather than seeded and
// overwritten.
interface PendingMergedGroup {
    groups: AccountGroup[];
    key: string;
    parentKey: string | null;
}

export interface MergedGroupsForDisplay {
    groupsById: Map<string, AccountGroup>;
    mergedGroupsByParentKey: Map<string | null, MergedGroup[]>;
    groupKeyById: Map<string, string>;
}

interface MergeGroupsForDisplayParams {
    groups: AccountGroup[];
    selectedEntityIds: number[] | undefined;
}

export function getEntityRank(
    entityId: number | undefined,
    selectedEntityRank: ReadonlyMap<number, number>,
): number {
    if (entityId === undefined) {
        return Number.MAX_SAFE_INTEGER;
    }
    return selectedEntityRank.get(entityId) ?? Number.MAX_SAFE_INTEGER;
}

export function mergeGroupsForDisplay({
    groups,
    selectedEntityIds,
}: MergeGroupsForDisplayParams): MergedGroupsForDisplay {
    const selectedEntityIdSet = new Set(selectedEntityIds ?? []);
    const hasSelectedEntities = selectedEntityIdSet.size > 0;
    const visibleGroups = groups.filter(
        (group) =>
            group.entityId === undefined ||
            !hasSelectedEntities ||
            selectedEntityIdSet.has(group.entityId),
    );
    const groupsById = new Map(visibleGroups.map((group) => [group.id, group]));
    const selectedEntityRank = new Map(
        selectedEntityIds?.map((entityId, index) => [entityId, index]) ?? [],
    );
    const groupInputOrder = new Map(
        visibleGroups.map((group, index) => [group.id, index]),
    );
    const groupPathById = new Map<string, string[]>();
    const resolvingGroupIds = new Set<string>();

    const resolveGroupPath = (group: AccountGroup): string[] => {
        const cachedPath = groupPathById.get(group.id);
        if (cachedPath) {
            return cachedPath;
        }
        if (resolvingGroupIds.has(group.id)) {
            // Group trees are expected to be acyclic; truncate corrupted cycles
            // so the chart remains usable instead of failing the whole response.
            return [group.name.toLowerCase()];
        }
        resolvingGroupIds.add(group.id);
        const parent =
            group.parentGroupId === null
                ? undefined
                : groupsById.get(group.parentGroupId);
        const path = [
            ...(parent ? resolveGroupPath(parent) : []),
            group.name.toLowerCase(),
        ];
        resolvingGroupIds.delete(group.id);
        groupPathById.set(group.id, path);
        return path;
    };

    const groupKeyById = new Map<string, string>();
    for (const group of visibleGroups) {
        groupKeyById.set(
            group.id,
            getGroupDisplayKey({
                type: group.type,
                namePath: resolveGroupPath(group),
            }),
        );
    }

    const pendingMergedGroupsByKey = new Map<string, PendingMergedGroup>();
    for (const group of visibleGroups) {
        const key = groupKeyById.get(group.id);
        if (!key) {
            continue;
        }
        const parentKey =
            group.parentGroupId === null
                ? null
                : (groupKeyById.get(group.parentGroupId) ?? null);
        const existing = pendingMergedGroupsByKey.get(key);
        if (existing) {
            existing.groups.push(group);
            continue;
        }
        pendingMergedGroupsByKey.set(key, {
            groups: [group],
            key,
            parentKey,
        });
    }

    const mergedGroupsByParentKey = new Map<string | null, MergedGroup[]>();
    for (const pending of pendingMergedGroupsByKey.values()) {
        pending.groups.sort((a, b) => {
            const rankDifference =
                getEntityRank(a.entityId, selectedEntityRank) -
                getEntityRank(b.entityId, selectedEntityRank);
            if (rankDifference !== 0) {
                return rankDifference;
            }
            return (
                (groupInputOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
                (groupInputOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER)
            );
        });
        const storedSortOrders = pending.groups.flatMap((group) =>
            group.sortOrder === null ||
            group.sortOrder === undefined ||
            group.sortOrder === EMPTY_GROUP_SORT_ORDER_SENTINEL
                ? []
                : [group.sortOrder],
        );
        const mergedGroup: MergedGroup = {
            group: pending.groups[0],
            groups: pending.groups,
            mergedSortOrder:
                storedSortOrders.length > 0
                    ? Math.min(...storedSortOrders)
                    : EMPTY_GROUP_SORT_ORDER_SENTINEL,
            key: pending.key,
            parentKey: pending.parentKey,
        };
        const siblings =
            mergedGroupsByParentKey.get(mergedGroup.parentKey) ?? [];
        siblings.push(mergedGroup);
        mergedGroupsByParentKey.set(mergedGroup.parentKey, siblings);
    }

    return {
        groupsById,
        mergedGroupsByParentKey,
        groupKeyById,
    };
}
